import { Input, Pagination } from 'antd';
import { useEffect, useState } from 'react';

import { type PartyRole } from '@/api/generated/enums';
import { useParties, usePartyDuplicates, type Party, type PartyDuplicate } from '@/api/masters';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { WriteAction } from '@/components/WriteAction';
import { absent } from '@core/format/dates';
import type { Session } from '@core/session/types';

import { MergeDrawer } from './parties/MergeDrawer';
import { describeRoles, outstandingSummary, roleFilters, roleLabels } from './parties/roles';

const pageSize = 25;

/**
 * Everybody the company deals with, in one list (the party merge, phase 4).
 *
 * <p>
 * <strong>This screen is what the merge was for.</strong> The three phases before it moved a
 * person's name and number out of the customer, vendor and staff tables and into one identity they
 * all point at — and deliberately changed nothing anybody could see. What that bought is here: one
 * search across every master, so "does this company already know this number" is one question with
 * one answer instead of three screens and a guess.
 * </p>
 *
 * <p>
 * <strong>It is a read and a repair, not a fourth master.</strong> There is no "new party" button: a
 * person comes into existence because a customer, a vendor or a member of staff does, and is
 * corrected on whichever of those screens the operator is standing on. The one write here is the
 * merge, which is the thing none of the three can express.
 * </p>
 */
export function PartiesPage({ session }: { session: Session }) {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<PartyRole | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [merging, setMerging] = useState<PartyDuplicate | null>(null);

  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, role]);

  const parties = useParties({ search: debouncedSearch, role, page, pageSize });
  const duplicates = usePartyDuplicates();

  const outstanding = outstandingSummary(duplicates.data ?? []);

  const columns: DataViewColumn<Party>[] = [
    {
      title: 'Who',
      dataIndex: 'name',
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-900">{row.name}</span>
          <span className="font-mono-nums text-default text-[12px]">
            {row.mobile}
            {row.city ? ` · ${row.city}` : ''}
          </span>
        </div>
      ),
    },
    {
      title: 'Known as',
      key: 'roles',
      width: 240,
      render: (_, row) =>
        row.roles.length === 0 ? (
          // A real state rather than a gap: the only record using this identity has been deleted,
          // and the party keeps the number until something reuses it.
          <span className="text-default text-[13px]">{describeRoles(row.roles)}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.roles.map((value) => (
              <StatusBadge key={value} tone="neutral">
                {roleLabels[value]}
              </StatusBadge>
            ))}
          </div>
        ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      responsive: ['xl'],
      hideOnCard: true,
      render: (_, row) =>
        row.email === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="text-[13px]">{row.email}</span>
        ),
    },
  ];

  if (parties.isError) {
    return <ApiErrorAlert error={parties.error} />;
  }

  const total = parties.data?.total ?? 0;

  return (
    <>
      <PageHeading
        title="People and businesses"
        subtitle="One row per person, however many ways you deal with them."
      />

      {/*
        The repair queue, above the list rather than on a screen of its own. A shop only has these
        because the merge found them — anyone who was already both a customer and a member of staff
        arrived as two identities on one number — and the count is what says how much is left.
      */}
      {outstanding && (
        <Notice tone="warning" title="Some records are the same person twice">
          {outstanding} Merging them is what makes one number mean one person.
          <div className="mt-2 flex flex-wrap gap-2">
            {(duplicates.data ?? []).map((duplicate) => (
              <WriteAction key={duplicate.mobile} session={session} required="Party:Edit">
                {(disabled) => (
                  <Button
                    icon="icon icon-merge"
                    disabled={disabled}
                    onClick={() => setMerging(duplicate)}
                  >
                    {duplicate.mobile} · {duplicate.parties.length} records
                  </Button>
                )}
              </WriteAction>
            ))}
          </div>
        </Notice>
      )}

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search a name or a number"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-72 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />

          {/* Where to look, not what to show: somebody who is two things still appears once, with
              both, whichever chip is on. */}
          <div className="flex flex-wrap gap-1">
            {roleFilters.map((filter) => (
              <button
                key={filter.label}
                type="button"
                onClick={() => setRole(filter.value)}
                aria-pressed={role === filter.value}
                className={`rounded border px-2.5 py-1 text-[13px] ${
                  role === filter.value
                    ? 'border-primary bg-primary text-white'
                    : 'border-border-color text-default hover:text-dark'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <DataView<Party>
          columns={columns}
          dataSource={parties.data?.items ?? []}
          loading={parties.isLoading}
          empty={
            <EmptyState
              icon="icon icon-contact"
              title={debouncedSearch || role ? 'Nobody matches that' : 'Nobody on file yet'}
              hint={
                debouncedSearch || role
                  ? 'Try part of the number instead — the search matches it with or without spaces.'
                  : 'Everyone you add as a customer, a vendor or a member of staff appears here, once.'
              }
            />
          }
        />

        {total > pageSize && (
          <div className="border-border-color flex justify-end border-t py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} people and businesses`}
            />
          </div>
        )}
      </Card>

      {merging && <MergeDrawer duplicate={merging} onClose={() => setMerging(null)} />}
    </>
  );
}
